import { unsafeCSS, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { PopBaseComponent } from '../../base/pop-base-component';
import {
  AnnualizedPeriods,
  CurrentYearPeriods,
} from '../total-returns/total-returns.model';
import type {
  TotalReturnsTable,
  TotalReturnsTableDataRow,
} from './total-returns-table.model';
import {
  TotalReturnsTableType,
  TotalReturnsTableDataType,
} from './total-returns-table.model';

import style from './total-returns-table.scss?inline';
import {
  Constants,
  CurrentYearPerformanceColumnHeaders,
  CompositePerformanceColumnIds,
  RowHeaders,
  CurrentYearPerformanceColumnIds,
  CompositePerformanceColumnHeaders,
} from './total-returns-table.constants';
import { Constants as BaseConstants } from '../../base/base.constants';
import { dateShort } from '../../common/utils/formatDate';
import { formatDecimalAsPercentage } from '../../common/utils/formatNumber';
import {
  hasAnnualizedPerformanceDataForPeriod,
  AnnualizedPerformancePeriod,
  hasCurrentYearPerformanceDataForPeriod,
  CurrentYearPerformancePeriod,
} from '../total-returns/total-returns.utils';
import { AssetClassEnum } from '../../common/enums/asset-class';

/**
 * @tag pop-total-returns-table-component
 * @property {TotalReturnsTable} [componentObject] Total returns table content
 * @attribute {TotalReturnsTable} [data-component-object] Total returns table content
 */
@customElement(Constants.TAG_NAME)
export class TotalReturnsTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * Alternative display content when no value
   */
  @property({ attribute: Constants.NO_VALUE_DISPLAY })
  noValueDisplay = 'N/A';

  /**
   * The component object that contains the pre-fetched data to be rendered.
   */
  @property({
    attribute: BaseConstants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: TotalReturnsTable;

  /*
   * Component Methods
   */

  private handleDisplayValue(
    value: TotalReturnsTableDataRow['cellValue'],
    isPercentage: TotalReturnsTableDataRow['isPercentage'],
  ) {
    if (value === null || value === undefined) {
      return this.noValueDisplay;
    } else if (isPercentage) {
      return formatDecimalAsPercentage(Number(value), 2);
    } else {
      return value;
    }
  }

  private renderTableHeader(
    id: string,
    header: string,
    hasData: boolean,
    variant?: string,
    subtitle?: string,
  ) {
    if (!hasData) {
      return '';
    }

    let subtitleHtml;
    if (subtitle) {
      subtitleHtml = html`<span slot="subtitle">${subtitle}</span>`;
    }

    return html` <th id="${id}" scope="col">
      <beacon-table-header variant="${variant}">
        ${header} ${subtitleHtml}
      </beacon-table-header>
    </th>`;
  }

  private renderCurrentYearPerformanceHeaders() {
    const currentYearValues =
      this.componentObject?.currentYearPerformanceValues?.monthEnd;

    return html`
      <tr>
        <th id="name" scope="col"></th>
        ${this.renderTableHeader(
          CurrentYearPerformanceColumnIds?.MonthlyYTD,
          CurrentYearPerformanceColumnHeaders?.MonthlyYTD,
          hasCurrentYearPerformanceDataForPeriod(
            currentYearValues,
            CurrentYearPerformancePeriod.YearToDate,
          ),
        )}
        ${this.renderTableHeader(
          CurrentYearPerformanceColumnIds?.OneMonth,
          CurrentYearPerformanceColumnHeaders?.OneMonth,
          hasCurrentYearPerformanceDataForPeriod(
            currentYearValues,
            CurrentYearPerformancePeriod.OneMonth,
          ),
        )}
        ${this.renderTableHeader(
          CurrentYearPerformanceColumnIds?.ThreeMonths,
          CurrentYearPerformanceColumnHeaders?.ThreeMonths,
          hasCurrentYearPerformanceDataForPeriod(
            currentYearValues,
            CurrentYearPerformancePeriod.ThreeMonths,
          ),
        )}
      </tr>
    `;
  }

  renderAnnualizedPerformanceHeaders() {
    const compositeValues =
      this.componentObject?.dataValueType === TotalReturnsTableDataType.Monthly
        ? this.componentObject?.annualizedPerformanceValues?.monthEnd
        : this.componentObject?.annualizedPerformanceValues?.quarterEnd;

    const hasTenYearData = hasAnnualizedPerformanceDataForPeriod(
      compositeValues,
      AnnualizedPerformancePeriod.TenYear,
    );

    return html`
      <tr>
        <th id="name" scope="col"></th>
        ${this.renderTableHeader(
          CompositePerformanceColumnIds?.OneYear,
          CompositePerformanceColumnHeaders?.OneYear,
          hasAnnualizedPerformanceDataForPeriod(
            compositeValues,
            AnnualizedPerformancePeriod.OneYear,
          ),
        )}
        ${this.renderTableHeader(
          CompositePerformanceColumnIds?.ThreeYear,
          CompositePerformanceColumnHeaders?.ThreeYear,
          hasAnnualizedPerformanceDataForPeriod(
            compositeValues,
            AnnualizedPerformancePeriod.ThreeYear,
          ),
        )}
        ${this.renderTableHeader(
          CompositePerformanceColumnIds?.FiveYear,
          CompositePerformanceColumnHeaders?.FiveYear,
          hasAnnualizedPerformanceDataForPeriod(
            compositeValues,
            AnnualizedPerformancePeriod.FiveYear,
          ),
        )}
        ${this.renderTableHeader(
          CompositePerformanceColumnIds?.TenYear,
          CompositePerformanceColumnHeaders?.TenYear,
          hasTenYearData,
        )}
        ${this.renderTableHeader(
          CompositePerformanceColumnIds?.SinceInception,
          CompositePerformanceColumnHeaders?.SinceInception,
          !hasTenYearData &&
            hasAnnualizedPerformanceDataForPeriod(
              compositeValues,
              AnnualizedPerformancePeriod.SinceInception,
            ),
          'group',
          dateShort(this.componentObject?.portfolioInceptionDate),
        )}
      </tr>
    `;
  }

  private renderTableHead() {
    if (
      this.componentObject?.tableType ===
      TotalReturnsTableType.CurrentYearPerformance
    ) {
      return this.renderCurrentYearPerformanceHeaders();
    } else {
      return this.renderAnnualizedPerformanceHeaders();
    }
  }

  private renderTableDataCell(
    value: TotalReturnsTableDataRow['cellValue'],
    isPercentage: TotalReturnsTableDataRow['isPercentage'],
  ) {
    return html`
      <td>
        <beacon-table-record variant="numeral">
          ${this.handleDisplayValue(value, isPercentage)}
        </beacon-table-record>
      </td>
    `;
  }

  private renderTableDataRow(
    rowName: string,
    cellObjects: TotalReturnsTableDataRow[],
  ) {
    return html`
      <tr>
        <th scope="row">
          <beacon-table-stub>
            <span class="b-typography-sans-serif--body--100 b-font-weight-bold">
              ${unsafeHTML(rowName)}</span
            >
          </beacon-table-stub>
        </th>
        ${cellObjects.map((cellObject) => {
          return this.renderTableDataCell(
            cellObject.cellValue,
            cellObject.isPercentage,
          );
        })}
      </tr>
    `;
  }

  private generateDataCellForCurrentYearPeriod(
    period: CurrentYearPerformancePeriod,
    cellHeader: string,
    data?: CurrentYearPeriods | null,
  ) {
    const currentYearValues =
      this.componentObject?.currentYearPerformanceValues?.monthEnd;

    return hasCurrentYearPerformanceDataForPeriod(currentYearValues, period)
      ? [
          {
            cellHeader: cellHeader,
            cellValue: data && data[period],
            isPercentage: true,
          },
        ]
      : [];
  }

  private renderCurrentYearPerformanceRows() {
    const monthEnd =
      this.componentObject?.currentYearPerformanceValues?.monthEnd;

    const assetClass = this.componentObject?.assetClass;

    const dataRows = [
      {
        header:
          assetClass === AssetClassEnum.FIXED_INCOME
            ? RowHeaders?.PureGrossFixedIncome
            : RowHeaders?.PureGross,
        data: monthEnd?.pureGross,
      },
      {
        header:
          assetClass === AssetClassEnum.FIXED_INCOME
            ? RowHeaders?.NetFixedIncome
            : RowHeaders?.Net,
        data: monthEnd?.net,
      },
      {
        header: RowHeaders?.Benchmark,
        data: monthEnd?.benchmark,
      },
      {
        header: RowHeaders?.ValuePureGross,
        data: monthEnd?.pureGrossValueAdded,
      },
      {
        header: RowHeaders?.ValueNet,
        data: monthEnd?.netValueAdded,
      },
    ];

    return html`
      ${dataRows.map(
        (row) => html`
          ${this.renderTableDataRow(row.header, [
            ...this.generateDataCellForCurrentYearPeriod(
              CurrentYearPerformancePeriod.YearToDate,
              CurrentYearPerformanceColumnIds.MonthlyYTD,
              row.data,
            ),
            ...this.generateDataCellForCurrentYearPeriod(
              CurrentYearPerformancePeriod.OneMonth,
              CurrentYearPerformanceColumnIds.OneMonth,
              row.data,
            ),
            ...this.generateDataCellForCurrentYearPeriod(
              CurrentYearPerformancePeriod.ThreeMonths,
              CurrentYearPerformanceColumnIds.ThreeMonths,
              row.data,
            ),
          ])}
        `,
      )}
    `;
  }

  private generateDataCellForAnnualizedPeriod(
    period: AnnualizedPerformancePeriod,
    cellHeader: string,
    data?: AnnualizedPeriods | null,
  ) {
    const compositeValues =
      this.componentObject?.dataValueType === TotalReturnsTableDataType.Monthly
        ? this.componentObject?.annualizedPerformanceValues?.monthEnd
        : this.componentObject?.annualizedPerformanceValues?.quarterEnd;

    return hasAnnualizedPerformanceDataForPeriod(compositeValues, period)
      ? [
          {
            cellHeader: cellHeader,
            cellValue: data && data[period],
            isPercentage: true,
          },
        ]
      : [];
  }

  private renderAnnualizedPerformanceRows() {
    const compositeValues =
      this.componentObject?.dataValueType === TotalReturnsTableDataType.Monthly
        ? this.componentObject?.annualizedPerformanceValues?.monthEnd
        : this.componentObject?.annualizedPerformanceValues?.quarterEnd;

    const assetClass = this.componentObject?.assetClass;

    const dataRows = [
      {
        header:
          assetClass === AssetClassEnum.FIXED_INCOME
            ? RowHeaders?.PureGrossFixedIncome
            : RowHeaders?.PureGross,
        data: compositeValues?.pureGross,
      },
      {
        header:
          assetClass === AssetClassEnum.FIXED_INCOME
            ? RowHeaders?.NetFixedIncome
            : RowHeaders?.Net,
        data: compositeValues?.net,
      },
      {
        header: RowHeaders?.Benchmark,
        data: compositeValues?.benchmark,
      },
      {
        header: RowHeaders?.ValuePureGross,
        data: compositeValues?.pureGrossValueAdded,
      },
      {
        header: RowHeaders?.ValueNet,
        data: compositeValues?.netValueAdded,
      },
    ];

    const hasTenYearData = hasAnnualizedPerformanceDataForPeriod(
      compositeValues,
      AnnualizedPerformancePeriod.TenYear,
    );

    return html`
      ${dataRows.map(
        (row) => html`
          ${this.renderTableDataRow(row.header, [
            ...this.generateDataCellForAnnualizedPeriod(
              AnnualizedPerformancePeriod.OneYear,
              CompositePerformanceColumnIds.OneYear,
              row.data,
            ),
            ...this.generateDataCellForAnnualizedPeriod(
              AnnualizedPerformancePeriod.ThreeYear,
              CompositePerformanceColumnIds.ThreeYear,
              row.data,
            ),
            ...this.generateDataCellForAnnualizedPeriod(
              AnnualizedPerformancePeriod.FiveYear,
              CompositePerformanceColumnIds.FiveYear,
              row.data,
            ),
            ...this.generateDataCellForAnnualizedPeriod(
              AnnualizedPerformancePeriod.TenYear,
              CompositePerformanceColumnIds.TenYear,
              row.data,
            ),
            ...(hasTenYearData
              ? []
              : this.generateDataCellForAnnualizedPeriod(
                  AnnualizedPerformancePeriod.SinceInception,
                  CompositePerformanceColumnIds.SinceInception,
                  row.data,
                )),
          ])}
        `,
      )}
    `;
  }

  private renderTableBody() {
    if (
      this.componentObject?.tableType ===
      TotalReturnsTableType.CurrentYearPerformance
    ) {
      return this.renderCurrentYearPerformanceRows();
    } else {
      return this.renderAnnualizedPerformanceRows();
    }
  }

  render() {
    if (!this.componentObject) {
      return html``;
    }
    return this.renderComponent(
      html`<beacon-table variant="fixed">
        <table tabindex="0">
          <thead>
            ${this.renderTableHead()}
          </thead>
          <tbody>
            ${this.renderTableBody()}
          </tbody>
        </table>
      </beacon-table>`,
    );
  }
}
